import { queryOptions } from '@tanstack/react-query'
import type { DeviceRow } from '../inventoree/columns'
import { apiExchange, apiGet, apiPost, apiPostForm, ApiError } from './client'
import type { Named } from './shell'

/**
 * Contractul lui /api/v2/inventoree/devices-table (DataTables, ca POST
 * inventoree/getDevicesData in portal) si /inventoree/device-filters.
 */

export const LISTS = [
  'workstations',
  'servers',
  'inactive',
  'decommissioned',
  'mobiles',
  'unmanaged',
  'network',
  'agentless',
] as const
export type ListName = (typeof LISTS)[number]

export const AGENTLESS_LISTS = ['unmanaged', 'network', 'agentless'] as const
export type AgentlessList = (typeof AGENTLESS_LISTS)[number]

export function isList(value: string): value is ListName {
  return (LISTS as readonly string[]).includes(value)
}

export function isAgentlessList(value: string): value is AgentlessList {
  return (AGENTLESS_LISTS as readonly string[]).includes(value)
}

export interface TableRequest {
  list: ListName
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
  filters: Filters
}

export interface Filters {
  office: string
  brand: string
  group: string
  owner: string
  department: string
  inactive: boolean
}

export const EMPTY_FILTERS: Filters = { office: '', brand: '', group: '', owner: '', department: '', inactive: false }

export interface TablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: DeviceRow[]
  /**
   * Banda rosie din capul listei: statiile fara utilizator din ACELASI scop ca
   * lista (firma, sediu, filtre, cautare) - `UnassignedBand` pe server. Null
   * (sau lipsa) pe listele care nu fixeaza nealocatele.
   */
  unassigned?: UnassignedBandData | null
}

export interface UnassignedBandDevice {
  id: number
  name: string
  type: string
  office: string
  ip: string
  /** `it_echipamente.UserLogat` - ultimul om vazut de agent pe masina. */
  loggedUser: string
  /** `DataAdaugare` - cand a intrat in inventar. */
  addedAt: string | null
  /** `DataAccesareDM` - ultimul contact al agentului. */
  lastContactAt: string | null
  /** O singura fisa de persoana a firmei se potriveste cu userul logat. */
  suggestion: { id: number; name: string } | null
}

export interface UnassignedBandData {
  count: number
  /** `MAX(DataAdaugare)` al statiilor fara utilizator. */
  lastDiscovered: string | null
  /** Asignarea automata din AD e aprinsa pe firma. */
  adAssign: boolean
  devices: UnassignedBandDevice[]
}

export interface FilterOptions {
  offices: Named[]
  brands: Named[]
  departments: Named[]
  groups: Named[]
  owners: Named[]
}

/** Aceiasi parametri pe care ii trimitea DataTables (si aceleasi *Filter din InventoreeLiveDevices.data). */
export function tableParams(r: TableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('devices', r.list)
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  for (const key of ['office', 'brand', 'group', 'owner', 'department'] as const) {
    if (r.filters[key] !== '') p.set(`${key}Filter`, r.filters[key])
  }
  if (r.filters.inactive) p.set('inactiveFilter', '1')
  return p
}

export function tablePageQuery(r: TableRequest) {
  return queryOptions({
    queryKey: ['inventoree', 'table', r],
    queryFn: () => apiGet<TablePage>(`/inventoree/devices-table?${tableParams(r).toString()}`),
  })
}

export const filterOptionsQuery = queryOptions({
  queryKey: ['inventoree', 'filters'],
  queryFn: () => apiGet<FilterOptions>('/inventoree/device-filters'),
  staleTime: 5 * 60 * 1000,
})

export interface HostTreeNode {
  id: number
  name: string
  type: number
  virtual: number
  device_type: string
  host_type: string
  host_type_label: string
  seen: string
  icon?: string
  guests: HostTreeNode[]
}

export interface HostTreePage {
  roots: HostTreeNode[]
}

export function hostTreeQuery(r: { list: ListName; search: string; filters: Filters }) {
  const p = new URLSearchParams()
  p.set('devices', r.list)
  p.set('search[value]', r.search)
  for (const key of ['office', 'brand', 'group', 'owner', 'department'] as const) {
    if (r.filters[key] !== '') p.set(`${key}Filter`, r.filters[key])
  }
  if (r.filters.inactive) p.set('inactiveFilter', '1')
  return queryOptions({
    queryKey: ['inventoree', 'host-tree', r],
    queryFn: () => apiGet<HostTreePage>(`/inventoree/host-tree?${p}`),
  })
}

/** GET /api/v2/inventoree/printers - PrinterFleet::group. */
export interface PrinterFleetMachine {
  device_id: number
  name: string
  is_server: boolean
  queue: string
  default: boolean
  status: string
  supplies: string
  updated: string
}

export interface PrinterFleetRow {
  key: string
  name: string
  model: string
  transport: string
  host: string
  location: string
  description: string
  machines: PrinterFleetMachine[]
  default_on: number
  shared_from: string[]
  machine_count: number
  last_seen: string
  attention: { level: number; text: string }
}

export interface PrinterFleetPage {
  printers: PrinterFleetRow[]
  totals: { printers: number; network: number; local: number; attention: number; machines: number }
}

export const printersQuery = queryOptions({
  queryKey: ['inventoree', 'printers'],
  queryFn: () => apiGet<PrinterFleetPage>('/inventoree/printers'),
})

/** GET /api/v2/inventoree/reports (+ /data, /detail). */
export interface ReportType {
  id: number
  name: string
}

export interface ReportsMeta {
  componentTypes: ReportType[]
  canUpgrades: boolean
}

export interface ReportFilters {
  company_id: number
  search: string
  component_type: number
  component_type_name?: string
  from: string
  to: string
}

export interface ReportDeviceRow {
  id: number
  device_name: string
  company_name: string
  os: string
  profile_count: number
  application_count: number
  component_count: number
  last_profile_day: string
}

export interface ReportProfileRow {
  profile_id: number
  username: string
  company_name: string
  device_count: number
  first_day: string
  last_day: string
  working_time: number
}

export interface ReportHistoryRow {
  id: number
  device_id: number
  device_name: string
  company_name: string
  component_type_name: string
  component_name: string
  seen_at: string
  action: number
  action_word: string
}

export type ReportRow = ReportDeviceRow | ReportProfileRow | ReportHistoryRow

export interface ReportsPage {
  target: number
  rows: ReportRow[]
  total: number
  page: number
  limit: number
  pages: number
  filters: ReportFilters
}

export interface ReportDetailRow {
  id: number
  name: string
  company: string
  first_day: string
  last_day: string
  working_time: number
}

export interface ReportDetail {
  isProfile: boolean
  rows: ReportDetailRow[]
}

export const reportsMetaQuery = queryOptions({
  queryKey: ['inventoree', 'reports', 'meta'],
  queryFn: () => apiGet<ReportsMeta>('/inventoree/reports'),
})

export function reportsDataQuery(params: {
  target: number
  search: string
  component_type: string
  from: string
  to: string
  page: number
}) {
  const q = new URLSearchParams()
  q.set('target', String(params.target))
  q.set('search', params.search)
  q.set('component_type', params.component_type)
  q.set('from', params.from)
  q.set('to', params.to)
  q.set('page', String(params.page))
  return queryOptions({
    queryKey: ['inventoree', 'reports', 'data', params],
    queryFn: () => apiGet<ReportsPage>(`/inventoree/reports/data?${q.toString()}`),
  })
}

export function reportsDetailQuery(target: number, id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'reports', 'detail', target, id],
    queryFn: () => apiGet<ReportDetail>(`/inventoree/reports/detail/${target}/${id}`),
  })
}

/** GET /api/v2/devices/{id}/agentless-details - Inventoree::agentlessDetails. */
export interface AgentlessField {
  label: string
  value: string
}

export interface AgentlessDocument {
  id: number
  name: string
  type: string
  uploaded: string
  /* details.php scrie si marimea (convert_bytes), si legatura de descarcare
     (download_device_document_url). Placa le trimite pe amandoua, mereu -
     goale cand nu se pot afla, niciodata lipsa: un camp care poate lipsi e
     un camp pe care nimeni nu observa ca a disparut. */
  size: string
  download: string
}

export interface AgentlessDetailsPage {
  id: number
  name: string
  type: string
  brand: string
  model: string
  serial: string
  acquisition: string
  warranty: string
  classified: string
  ownerId: number
  owner: string
  officeId: number
  office: string
  department: string
  group: string
  customFields: AgentlessField[]
  documents: AgentlessDocument[]
}

export function agentlessDetailsQuery(id: number) {
  return queryOptions({
    queryKey: ['devices', id, 'agentless-details'],
    queryFn: () => apiGet<AgentlessDetailsPage>(`/devices/${id}/agentless-details`),
  })
}

/* ---------------------------------------------------------------------------
 * Inventoree :: Upgrades - /api/v2/inventoree/upgrades
 * ------------------------------------------------------------------------- */

export interface UpgradeCounters {
  info: number
  minor: number
  major: number
  critical: number
}

export interface UpgradeRow {
  DT_RowId: string
  ID: number
  flag: number | string
  type_name: string
  category_name: string
  from_value: number | string | null
  to_value: number | string | null
  dinamic: number | string
  sign: string | null
  status: number | string
  affected_devices: number | string
}

export interface UpgradeDevice {
  ID: number
  value: string | number | null
  sign: string | null
  Denumire: string
  device_type: number | string
  Nume: string | null
  Prenume: string | null
}

export function upgradesPageQuery() {
  return queryOptions({
    queryKey: ['inventoree', 'upgrades'],
    queryFn: () => apiGet<{ counters: UpgradeCounters }>('/inventoree/upgrades'),
  })
}

export function upgradesTableQuery(r: {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}) {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  if (r.orderName) p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return queryOptions({
    queryKey: ['inventoree', 'upgrades', 'table', r],
    queryFn: () => apiGet<TablePage>(`/inventoree/upgrades/table?${p.toString()}`),
  })
}

export function upgradeDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'upgrades', 'devices', id],
    queryFn: () => apiGet<{ alarms: UpgradeDevice[] }>(`/inventoree/upgrades/${id}/devices`),
  })
}

/* ---------------------------------------------------------------------------
 * Inventoree :: Live Devices / Discovered
 * ------------------------------------------------------------------------- */

export type DiscoveredFilter = 'live_devices' | 'can_deploy'

export interface DiscoveredPage {
  types: { device_type: string; devices: number }[]
  os: { os_family: string; devices: number }[]
  history: { saw: string; devices: number }[]
  watchOutside: boolean
  /** Dreptul de deploy: fereastra de pe rand cere un cont din Pass Safe. */
  canDeploy?: boolean
  /** Listele ferestrei "Add as unmanaged device". */
  promote?: {
    types: { id: number; name: string }[]
    brands: { id: number; name: string }[]
    offices: { id: number; name: string }[]
  }
}

export interface DiscoveredRow {
  DT_RowId: string
  ID: number
  device_name: string
  /** Numele intreg (wks02.cecdemo.local), cand e altul decat numele scurt; gol altfel. */
  fqdn?: string | null
  friendly_name?: string | null
  Agent: number | string | boolean | null
  first_saw: string | null
  last_saw: string | null
  internal_ip: string
  mac: string
  os: string
  manufacturer_name?: string | null
  deploy_status: number | string | null
  deploy_problem?: string | null
  method: number | string | null
  can_deploy?: number | string
  /** Codul de pregatire din porturi (DeployReadiness) si rangul lui. */
  deploy_ready?: string
  deploy_rank?: number | string
  /** Verdictul probei TOD, cand s-a cerut una. */
  precheck_result?: string | null
  precheck_via?: string | null
  precheck_at?: string | null
  precheck_problem?: string | null
  /** Tipul deja scris in inventar (`it_discovered_devices.type`); 0 cand nu e. */
  type?: number
  /** Propunerile pentru "Add as unmanaged device": tip/marca din MAC, sediu din adresa. */
  suggested_type?: number
  suggested_brand?: number
  suggested_brand_name?: string
  suggested_office?: number
  suggested_office_name?: string
  is_relay?: number | string
  od_name?: string | null
  od_id?: number | null
  ad_relay_id?: number | string | null
  relay_name?: string | null
  device_type_name?: string | null
  device_icon: string
  actions: string
  checking?: number | string
  open_ports?: string | null
  hints?: string | null
  uuid?: string | null
  virtual_host_unique?: string | null
  virtual_host_mac?: string | null
  is_win_server?: number | string | null
  /**
   * Bifa randului, pusa de ecran inainte de desenare.
   *
   * Casuta se scrie in HTML-ul celulei, iar React rescrie celula la fiecare
   * desenare - deci fara semnul asta bifa s-ar sterge la prima reimprospatare
   * a listei, cum se sterge si pe celelalte liste care nu-l poarta.
   */
  __checked?: boolean
}

export function discoveredPageQuery() {
  return queryOptions({
    queryKey: ['inventoree', 'discovered'],
    queryFn: () => apiGet<DiscoveredPage>('/inventoree/discovered'),
  })
}

export function discoveredTableQuery(r: {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
  filter: DiscoveredFilter
}) {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  if (r.orderName) p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  p.set('filterDevices', r.filter)
  return queryOptions({
    queryKey: ['inventoree', 'discovered', 'table', r],
    queryFn: () => apiGet<{ draw: number; recordsTotal: number; recordsFiltered: number; data: DiscoveredRow[] }>(
      `/inventoree/discovered/table?${p.toString()}`,
    ),
  })
}

/* ---------------------------------------------------------------------------
 * Inventoree :: Software licenses / Unassigned
 * ------------------------------------------------------------------------- */

export interface LicenseRow {
  ID: number
  DT_RowId: string
  short_name: string
  total: number | null
  active: number | null
  devices: number | null
  devices_licensed: number | null
  last_version: string | null
  category_name: string | null
  [key: string]: unknown
}

export interface LicenseTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: LicenseRow[]
}

export interface LicenseGap {
  name: string
  value: number | string
}

export function licenseTableParams(r: { draw: number; start: number; length: number; orderColumn: number; orderDir: 'asc' | 'desc'; search: string }): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set('search[value]', r.search)
  return p
}

export function licenseTableQuery(r: { draw: number; start: number; length: number; orderColumn: number; orderDir: 'asc' | 'desc'; search: string }) {
  return queryOptions({
    queryKey: ['inventoree', 'licenses', 'table', r],
    queryFn: () => apiGet<LicenseTablePage>(`/inventoree/software-licenses/table?${licenseTableParams(r).toString()}`),
  })
}

export const licenseGapsQuery = queryOptions({
  queryKey: ['inventoree', 'licenses', 'gaps'],
  queryFn: () => apiGet<LicenseGap[]>('/inventoree/software-licenses/gaps?limit=15'),
})

export interface UnassignedRow {
  ID: number
  deviceId: number
  DT_RowId: string
  Tip: number
  device_type: string
  device_icon: string
  Denumire: string
  UserLogat: string | null
  office: string | null
  DataAdaugare: string | null
  sLastIPIntern: string | null
  Mac: string | null
  external_ip: string | null
  location: string | null
  os_family: string | null
  device_role: string | null
  sAgentVersion: string | null
  matched: number
  DataAccesareDM: string | null
  actions: string
  __checked?: boolean
  [key: string]: unknown
}

export interface UnassignedTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: UnassignedRow[]
}

export interface UnassignedFilters {
  offices: Named[]
  groups: Named[]
  areas: Named[]
  users: Named[]
}

export function unassignedTableParams(r: { draw: number; start: number; length: number; orderColumn: number; orderDir: 'asc' | 'desc'; search: string }): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set('search[value]', r.search)
  return p
}

export function unassignedTableQuery(r: { draw: number; start: number; length: number; orderColumn: number; orderDir: 'asc' | 'desc'; search: string }) {
  return queryOptions({
    queryKey: ['inventoree', 'unassigned', 'table', r],
    queryFn: () => apiGet<UnassignedTablePage>(`/inventoree/unassigned/table?${unassignedTableParams(r).toString()}`),
  })
}

/**
 * Insigna "N unassigned" de deasupra listelor Inventoree, si drepturile de care
 * atarna ce se deseneaza.
 *
 * Capatul nu raspunde cu 403 (il cere orice om cu drept pe lista de aparate):
 * `canList` spune daca are voie pe lista Unknown - deci daca se arata insigna -
 * iar `canAssign` daca are voie sa aloce - deci daca apare butonul Assign din
 * randul desfacut. Exact dreptul pe care il cere si fisa
 * (`overview.unassigned_details_assign.read`).
 */
export interface UnassignedTally {
  count: number
  canList: boolean
  canAssign: boolean
}

export const unassignedTallyQuery = queryOptions({
  queryKey: ['inventoree', 'unassigned', 'tally'],
  queryFn: () => apiGet<UnassignedTally>('/inventoree/unassigned/tally'),
  staleTime: 60 * 1000,
})

export const unassignedFiltersQuery = queryOptions({
  queryKey: ['inventoree', 'unassigned', 'filters'],
  queryFn: () => apiGet<UnassignedFilters>('/inventoree/unassigned/filters'),
  staleTime: 5 * 60 * 1000,
})

/* ---------------------------------------------------------------------------
 * Inventoree :: Inventory map / Live agent-based (managed)
 * ------------------------------------------------------------------------- */

export interface MapOffice {
  ID: number
  Nume: string
}

export interface InventoryMapPage {
  offices: MapOffice[]
  deviceTypes: Named[]
  brands: Named[]
}

export const inventoryMapPageQuery = queryOptions({
  queryKey: ['inventoree', 'inventory-map'],
  queryFn: () => apiGet<InventoryMapPage>('/inventoree/inventory-map'),
})

/** inventoryMapUpdate / Confirm / Uplink / Ignore: aceleasi campuri ca POST-urile CI3. */
export interface MapWriteResult {
  status: 'OK' | 'ERROR'
  id?: number
  placed?: boolean
  cleared?: boolean
  message?: string
  credential?: { status: 'OK' | 'ERROR'; saved?: number; message?: string }
}

export function inventoryMapUpdate(fields: Record<string, string | number>) {
  return apiPost<MapWriteResult>('/inventoree/inventory-map/update', fields)
}

export function inventoryMapConfirm(fields: Record<string, string | number>) {
  return apiPost<MapWriteResult>('/inventoree/inventory-map/confirm', fields)
}

export function inventoryMapUplink(deviceId: number, switchId: number) {
  return apiPost<MapWriteResult>('/inventoree/inventory-map/uplink', { deviceId, switchId })
}

export function inventoryMapIgnore(fields: Record<string, string | number>) {
  return apiPost<MapWriteResult>('/inventoree/inventory-map/ignore', fields)
}

export function inventoryMapDataQuery(office: string, at: string) {
  const p = new URLSearchParams()
  if (office !== '') p.set('office', office)
  if (at !== '') p.set('at', at)
  const q = p.toString()
  return queryOptions({
    queryKey: ['inventoree', 'inventory-map', 'data', office, at],
    queryFn: () => apiGet<Record<string, unknown>>(`/inventoree/inventory-map/data${q ? '?' + q : ''}`),
    /* Fara asta, la fiecare focus pe fereastra se reia desenul si mutarile
       par nesalvate, chiar daca localStorage le tine. */
    staleTime: 60_000,
  })
}

export interface ManagedFilters {
  office: string
  group: string
  owner: string
  department: string
  deviceType: string
  inactive: boolean
}

export const EMPTY_MANAGED_FILTERS: ManagedFilters = {
  office: '',
  group: '',
  owner: '',
  department: '',
  deviceType: '',
  inactive: false,
}

export interface ManagedRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
  filters: ManagedFilters
}

export function managedParams(r: ManagedRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('devices', 'all')
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  if (r.filters.deviceType !== '') p.set('deviceType', r.filters.deviceType)
  for (const key of ['office', 'group', 'owner', 'department'] as const) {
    if (r.filters[key] !== '') p.set(`${key}Filter`, r.filters[key])
  }
  if (r.filters.inactive) p.set('inactiveFilter', '1')
  return p
}

export function managedTableQuery(r: ManagedRequest) {
  return queryOptions({
    queryKey: ['inventoree', 'managed', r],
    queryFn: () => apiGet<TablePage>(`/inventoree/managed-table?${managedParams(r).toString()}`),
  })
}

export const managedFiltersQuery = queryOptions({
  queryKey: ['inventoree', 'managed-filters'],
  queryFn: () => apiGet<Omit<FilterOptions, 'brands'>>('/inventoree/managed-filters'),
  staleTime: 5 * 60 * 1000,
})

/* ---------------------------------------------------------------------------
 * Inventoree :: standalone device row + Endpoint Performance dashboard
 * ------------------------------------------------------------------------- */

export function deviceRowQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'device-row', id],
    queryFn: () => apiGet<DeviceRow>(`/inventoree/device-row/${id}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export interface EndpointAlarms {
  processor: number
  ram: number
  swap: number
  services: number
  total: number
}

export interface EndpointTopAlarm {
  device_id: number
  Denumire: string
  processor: number | string
  ram: number | string
  swap: number | string
  services: number | string
  number: number | string
}

export interface EndpointDashboardData {
  alarms: EndpointAlarms
  alarms_number: { type: number; number: number }[]
  top_alarms: EndpointTopAlarm[]
  alarms_by_hours: { hour: number; processor: number; ram: number; swap: number; services: number }[]
  website_load_time: { address: string; Denumire: string; avg_loadTime: number }[]
  lan_response: { Denumire: string; ip: string; avg_value: number }[]
  internet_alarms_number: { host: string; number: number }[]
  performance_score: { Denumire: string; performance: number }[]
}

export interface EndpointTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: EndpointTopAlarm[]
}

export function endpointDashboardQuery(from: string, to: string) {
  const q = new URLSearchParams()
  if (from) q.set('start', from)
  if (to) q.set('end', to)
  return queryOptions({
    queryKey: ['inventoree', 'endpoint-performance', from, to],
    queryFn: () => apiGet<EndpointDashboardData>(`/inventoree/endpoint-performance?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function endpointDashboardTableQuery(from: string, to: string) {
  const q = new URLSearchParams()
  q.set('draw', '1')
  if (from) q.set('start', from)
  if (to) q.set('end', to)
  return queryOptions({
    queryKey: ['inventoree', 'endpoint-performance', 'table', from, to],
    queryFn: () => apiGet<EndpointTablePage>(`/inventoree/endpoint-performance/table?${q}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

/* ---------------------------------------------------------------------------
 * Listele: ADD Device / CSV / QR - capetele de pe writes + export
 * ------------------------------------------------------------------------- */

export interface DeviceTypeForm {
  type: number
  types?: Named[]
  owners: Named[]
  offices: Named[]
  brands: Named[]
  groups: Named[]
  subTypes: Named[]
  customFields: { id: number; label: string; field_type: number; value: string; options: string[] }[]
}

export function loadDeviceTypes() {
  return apiGet<InventoryMapPage>('/inventoree/inventory-map').then((page) => page.deviceTypes)
}

export function loadDeviceTypeForm(type: number) {
  const q = new URLSearchParams()
  q.set('type', String(type))
  return apiGet<DeviceTypeForm>(`/inventoree/device-type-form?${q.toString()}`)
}

export async function addDevice(fields: Record<string, string>) {
  const body = new FormData()
  for (const [key, value] of Object.entries(fields)) body.set(key, value)
  const out = await apiExchange('/inventoree/devices', { method: 'POST', body })
  if (out.text.startsWith('Field ')) throw new ApiError(out.status, out.text)
  if (out.text.trim() === '') return { status: 'OK' }
  return JSON.parse(out.text) as { status: string; id?: number }
}

/* ------------------------------------------------------------------------ *
 * Importul de aparate agentless - `Inventoree::uploadCsv`, in doi pasi.
 *
 * Fisierul pleaca de doua ori, ca la importul CMDB: serverul nu tine randurile
 * intre cereri, iar o lista "deja verificata" trimisa din browser ar fi
 * clientul spunand serverului ce e voie sa se scrie.
 * ------------------------------------------------------------------------ */

/** Ce se intampla cu un rand: `create`, `update` sau `error` (cu motivele). */
export interface CsvPlanRow {
  line: number
  unique: string
  name: string
  type: string
  office: string
  action: 'create' | 'update' | 'error'
  errors: string[]
  deviceId: number | null
}

export interface CsvPlan {
  status: 'OK' | 'ERROR'
  message: string
  fileErrors: string[]
  rows: CsvPlanRow[]
  create: number
  update: number
  invalid: number
  newGroups: string[]
  newBrands: string[]
  truncated: boolean
}

export interface CsvOutcome {
  status: 'OK' | 'ERROR'
  message: string
  created: number
  updated: number
  plan?: CsvPlan
}

function csvBody(file: File): FormData {
  const body = new FormData()
  body.set('csv', file)
  return body
}

/** Prima trecere: se citeste si se judeca, NU se scrie. */
export function csvPreview(file: File) {
  return apiPostForm<Partial<CsvPlan> & { status: 'OK' | 'ERROR'; message: string }>('/inventoree/csv/preview', csvBody(file))
}

/** A doua trecere: acelasi fisier, verificat iar, scris totul sau nimic. */
export function csvImport(file: File) {
  return apiPostForm<CsvOutcome>('/inventoree/csv/import', csvBody(file))
}

/** Sablonul gol (antetul), salvat pe disc. */
export async function csvTemplate(): Promise<void> {
  const out = await apiExchange('/inventoree/csv/template', { method: 'GET' })
  if (out.status !== 200) throw new ApiError(out.status, 'The template could not be downloaded.')
  const blob = new Blob([out.bytes], { type: out.type || 'text/csv' })
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = out.filename || 'agentless_devices_template.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}

function idsBody(key: string, ids: number[]): FormData {
  const body = new FormData()
  for (const id of ids) body.append(`${key}[]`, String(id))
  return body
}

export interface BulkPropertiesPage {
  devices: number[]
  brands: Named[]
}

export interface BulkAgentlessPage {
  devices: number[]
  owners: Named[]
  offices: Named[]
  brands: Named[]
  groups: Named[]
  types: Named[]
}

export function loadBulkProperties(ids: number[]) {
  return apiPostForm<BulkPropertiesPage>('/inventoree/bulk/properties-form', idsBody('device', ids))
}

export function saveBulkProperties(ids: number[], fields: Record<string, string>) {
  const body = idsBody('devices', ids)
  for (const [key, value] of Object.entries(fields)) body.set(key, value)
  return apiPostForm<{ ok: number }>('/inventoree/bulk/properties', body)
}

export function loadBulkAgentless(ids: number[]) {
  return apiPostForm<BulkAgentlessPage>('/inventoree/bulk/agentless-form', idsBody('device', ids))
}

export function saveBulkAgentless(ids: number[], fields: Record<string, string>) {
  const body = idsBody('devices', ids)
  for (const [key, value] of Object.entries(fields)) body.set(key, value)
  return apiPostForm<{ ok: number }>('/inventoree/bulk/agentless', body)
}

export function unassignedDevices(ids: number[]) {
  return apiPostForm<{ ok: number }>('/inventoree/multipleUnassignedDevices', idsBody('devicesid', ids))
}

export function unassignedDevice(deviceId: number) {
  const body = new FormData()
  body.set('deviceid', String(deviceId))
  return apiPostForm<{ ok: number }>('/inventoree/unassignedDevice', body)
}

/**
 * Atribuirea in masa de pe New devices - POST /api/v2/inventoree/unassigned/mass-assign.
 *
 * Se trimit numai campurile completate; capatul le trece prin aceeasi harta ca
 * `Overview::massAssign`, deci unul gol inseamna "nu atinge", nu "sterge".
 */
export function massAssignDevices(ids: number[], fields: Record<string, string>) {
  const body = new FormData()
  for (const id of ids) body.append('devices[]', String(id))
  for (const [key, value] of Object.entries(fields)) {
    if (value !== '') body.set(key, value)
  }
  return apiPostForm<{ changed: number }>('/inventoree/unassigned/mass-assign', body)
}

/** Butonul Remove de pe randul unei masini noi - Overview::removeDevice (`removed = 1`). */
export function removeUnassignedDevice(deviceId: number) {
  const body = new FormData()
  body.set('device', String(deviceId))
  return apiPostForm<{ ok: number }>('/inventoree/unassigned/remove', body)
}

export function loadMultipleQrForm(ids: number[]) {
  return apiExchange('/inventoree/qr/multiple-form', { method: 'POST', body: idsBody('device', ids) }).then((out) => out.text)
}

/**
 * Da fisierul omului.
 *
 * Sta separat fiindca il cer mai multe drumuri - PDF-ul de QR, care vine pe
 * POST, Excel-ul din Reports si cele doua exporturi de acoperire din ADM, care
 * vin pe GET. Scris de mai multe ori, s-ar fi despartit la prima corectie
 * (numele implicit, revocarea URL-ului) si numai una din descarcari ar fi
 * primit-o - de-aia e si `export`.
 */
export function saveBytes(bytes: ArrayBuffer, type: string, filename: string, fallback: string): void {
  const blob = new Blob([bytes], { type: type || 'application/octet-stream' })
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename || fallback
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(href)
}

/**
 * Excel-ul din Reports - GET /api/v2/inventoree/reports/export.
 *
 * Butonul "Export" statea mort, cu un titlu care spunea ca foaia o da mai
 * departe "portalul vechi": `Inventoree::reportsExport` a plecat insa odata cu
 * controllerul CI3, deci apasarea nu mai ducea nicaieri. Foaia se scrie deja in
 * API (ReportsXlsx), cu ACELEASI filtre pe care le are lista de dedesubt - deci
 * ce se descarca e chiar raspunsul de pe ecran, nu tot inventarul.
 */
export async function exportReports(params: {
  target: number
  search: string
  component_type: string
  from: string
  to: string
}): Promise<void> {
  const q = new URLSearchParams()
  q.set('target', String(params.target))
  q.set('search', params.search)
  q.set('component_type', params.component_type)
  q.set('from', params.from)
  q.set('to', params.to)
  const out = await apiExchange(`/inventoree/reports/export?${q.toString()}`, { method: 'GET' })
  saveBytes(out.bytes, out.type, out.filename, 'report.xlsx')
}

export async function postDownload(path: string, body: FormData) {
  const out = await apiExchange(path, { method: 'POST', body })
  if (out.type.includes('pdf') || out.type.includes('octet-stream') || out.filename !== '') {
    saveBytes(out.bytes, out.type || 'application/pdf', out.filename, 'download.pdf')
    return
  }
  if (out.text.trim() !== '' && out.text.trim() !== 'An error occured! Please try again..') {
    throw new ApiError(out.status, out.text.trim())
  }
  if (out.text.includes('An error occured')) throw new ApiError(out.status || 400, 'An error occured! Please try again..')
}

export interface LicenseApp {
  ID: number
  short_name: string
}

/**
 * Un rand din lista de aplicatii cu licente (`SELECT it_softsee_applications_list.*`
 * plus numaratorile din `DbalSoftwareLicensesSource::applicationRows`).
 */
export interface SoftwareLicenseRow {
  ID: number
  DT_RowId?: string
  short_name: string | null
  last_version: string | null
  category_name: string | null
  /** SUM(licenses_number) - cate licente sunt cumparate */
  total: number | string | null
  /** COUNT(it_softsee_device_licenses.ID) - cate sunt folosite */
  active: number | string | null
  devices: number | string | null
  devices_licensed: number | string | null
  [key: string]: unknown
}

export interface AppLicenseRow {
  ID: number
  DT_RowId: string
  type_name: string | null
  licenses_number: number | string | null
  active_licenses: number | string | null
  start_date: string | null
  end_date: string | null
  status: 'valid' | 'expired' | string
  [key: string]: unknown
}

export interface AppLicensePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: AppLicenseRow[]
}

export interface LicenseDevice {
  Denumire: string
  Tip: number | string | null
  type_name?: string | null
  Nume: string | null
  Prenume: string | null
}

export function licenseAppQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'licenses', 'app', id],
    queryFn: () => apiGet<LicenseApp>(`/inventoree/software-licenses/${id}`),
  })
}

export function appLicensesQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'licenses', 'app-licenses', id],
    queryFn: () => apiGet<AppLicensePage>(`/inventoree/software-licenses/${id}/licenses`),
  })
}

export function licensedDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'licenses', 'licensed', id],
    queryFn: () => apiGet<{ devices: LicenseDevice[] }>(`/inventoree/software-licenses/${id}/licensed-devices`),
  })
}

export function unlicensedDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'licenses', 'unlicensed', id],
    queryFn: () => apiGet<{ devices: LicenseDevice[] }>(`/inventoree/software-licenses/${id}/unlicensed-devices`),
  })
}

